<template>
  <form @submit.prevent="save">
    <base-card>
      <template v-slot:header>
        <base-card-heading>Lending Terms</base-card-heading>
      </template>
      <div class="row">
        <div class="col-sm-4">
          <base-field addon-right="months" label="Term">
            <base-input v-model="payload.term" numeric placeholder="term" required />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field label="Loan Type">
            <base-select v-model="payload.type" placeholder="type" required source="facilityTypes" />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field addon-right-icon="calendar-alt" label="Loan Start">
            <base-input v-model="payload.startDate" datepicker placeholder="start date" required />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field addon-left-icon="pound-sign" label="Loan Facility">
            <base-input v-model="payload.amount" numeric placeholder="amount" required />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field label="Security">
            <base-select v-model="payload.security" placeholder="security" required source="securityCharges" />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field addon-right-icon="calendar-alt" label="Projected Repayment">
            <base-input v-model="payload.projectedRepaymentDate" :datepicker="{ minDate: getProjectedMinDate }" placeholder="date" required />
          </base-field>
        </div>
      </div>
    </base-card>
    <base-card>
      <template v-slot:header>
        <base-card-heading>Non-utilisation Terms</base-card-heading>
      </template>
      <div class="row">
        <div class="col-sm-4">
          <base-field addon-right-icon="percent" label="Non-utilisation Rate">
            <base-input v-model="payload.nonUtilRate" numeric placeholder="non-util rate" required />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field label="Charges Serviced">
            <base-select v-model="payload.nonUtilAdded" truthy />
          </base-field>
        </div>
      </div>
    </base-card>
    <base-card>
      <template v-slot:header>
        <base-card-heading>Default Terms</base-card-heading>
      </template>
      <div class="row">
        <div class="col-sm-4">
          <base-field addon-right-icon="percent" label="Default Rate">
            <base-input v-model="payload.defaultRate" numeric placeholder="default rate" />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field label="Capitalise Loan on Default">
            <base-select v-model="payload.capitaliseLoanOnDefault" truthy />
          </base-field>
        </div>
        <div class="col-sm-4"></div>
        <div class="col-sm-4">
          <base-field label="Default Interest Compounded">
            <base-select v-model="payload.defaultCompounded" truthy />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field label="Loan in Default">
            <base-select v-model="payload.defaultEndOfTerm" truthy />
          </base-field>
        </div>
        <div class="col-sm-4">
          <base-field addon-right-icon="calendar-alt" label="Default Date">
            <base-input v-model="payload.defaultDate" datepicker placeholder="default date" />
          </base-field>
        </div>
      </div>
    </base-card>
    <facility-fees v-model="payload.fees" :loans.sync="payload.loans" />
    <base-card body-class="p-0">
      <template v-slot:header>
        <base-card-heading>Facility Loans</base-card-heading>
        <base-button secondary small @click="addLoan">Add Loan</base-button>
      </template>
      <base-stats v-model="stats" />
    </base-card>
    <template v-for="(index, key) in Object.keys(payload.loans)">
      <facility-loan
        v-model="payload.loans[index]"
        :end-date.sync="getLoanEndDate"
        :index="index"
        :key="key"
        :start-date.sync="getLoanStartDate"
        @added="scrollTo"
        @remove="removeLoan" />
    </template>
    <div class="text-right">
      <base-button :loading="saving" primary submit>Save Terms</base-button>
    </div>
  </form>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import FacilityDrawdown from './FacilityDrawdown.vue'
import FacilityFees from './FacilityFees.vue'
import FacilityLoan from './FacilityLoan.vue'

export default {
  name: 'ProjectTerms',

  components: {
    FacilityDrawdown,
    FacilityFees,
    FacilityLoan
  },

  created () {
    if (this.$_.isPlainObject(this.project.facility)) {
      this.payload = this.$_.cloneDeep(this.project.facility)
      this.initial = true
    } else {
      this.payload = this.$_.cloneDeep(this.facilitySchema)
      this.addLoan()
    }
  },

  data: () => ({
    initial: false,
    payload: {},
    saving: false,
    stats: []
  }),

  computed: {
    getLoanStartDate () {
      if (this.payload.startDate) {
        return this.$moment(this.payload.startDate, 'DD/MM/YYYY').toDate()
      }
    },

    getLoanEndDate () {
      if (this.payload.projectedRepaymentDate) {
        return this.$moment(this.payload.projectedRepaymentDate, 'DD/MM/YYYY').toDate()
      }
    },

    getProjectedMinDate () {
      if (this.payload.startDate) {
        return this.$moment(this.payload.startDate, 'DD/MM/YYYY').add(1, 'day').toDate()
      }
    },

    ...mapGetters({
      drawdownSchema: 'core/facility-drawdowns/schema',
      facilitySchema: 'core/facilities/schema',
      loanSchema: 'core/facility-loans/schema'
    }),

    ...mapState({
      drawdown: store => store.core.facilityDrawdowns.entity,
      facility: store => store.core.facilities.entity,
      fee: store => store.core.facilityFees.entity,
      loan: store => store.core.facilityLoans.entity,
      project: store => store.core.projects.entity
    })
  },

  methods: {
    addLoan () {
      const loan = this.$_.cloneDeep(this.loanSchema)

      this.payload.loans.push(loan)
    },

    removeLoan (index) {
      this.$dialog.confirm('Are you sure you want to remove this loan?', () => {
        this.payload.loans.splice(index, 1)
      })
    },

    getTotalDeployed () {
      let amounts = []

      for (let loan of this.payload.loans) {
        loan.drawdowns.forEach(drawdown => {
          let { amount, state } = drawdown

          if (state !== 'Projection') {
            amounts.push(this.$_.toNumber(amount))
          }
        })
      }

      return this.$_.sum(amounts)
    },

    getTotalLoans () {
      let amounts = []

      this.$_.map(this.payload.loans, 'amount').forEach(amount => {
        amounts.push(this.$_.toNumber(amount))
      })

      return this.$_.sum(amounts)
    },

    scrollTo (id) {
      if (this.initial) {
        setTimeout(() => this.$window.scrollToElementById(id), 100)
      }

      this.initial = true
    },

    async saveFees () {
      const payload = this.$_.cloneDeep(this.payload)
      let fees = []

      for (let key in payload.fees) {
        if (this.$_.has(payload.loans, payload.fees[key].loan)) {
          payload.fees[key].loan = this.$_.get(payload.loans, this.$_.toNumber(payload.fees[key].loan))._id
        }

        await this.$store.dispatch('core/facility-fees/save', payload.fees[key])

        fees.push(this.fee._id)

        this.payload.fees[key]._id = this.fee._id
      }

      return fees
    },

    async saveLoans () {
      const payload = this.$_.cloneDeep(this.payload)
      let loans = []

      for (let key in payload.loans) {
        let loan = payload.loans[key]
        let drawdowns = []

        for (let index in loan.drawdowns) {
          let drawdown = loan.drawdowns[index]

          await this.$store.dispatch('core/facility-drawdowns/save', drawdown)

          drawdowns.push(this.drawdown._id)

          this.payload.loans[key].drawdowns[index]._id = this.drawdown._id
        }

        loan.drawdowns = drawdowns

        await this.$store.dispatch('core/facility-loans/save', loan)

        loans.push(this.loan._id)

        this.payload.loans[key]._id = this.loan._id
      }

      return loans
    },

    async save () {
      this.saving = true

      const payload = this.$_.cloneDeep(this.payload)

      payload.loans = await this.saveLoans()
      payload.fees = await this.saveFees()

      await this.$store.dispatch('core/facilities/save', payload)
      await this.$store.dispatch('core/projects/save', {
        _id: this.project._id,
        facility: this.facility._id
      })

      this.payload._id = this.facility._id
      this.saving = false

      this.$flash.success('Facility terms saved.')
      this.$router.push({ name: 'projects.dashboard.servicing',
        params: {
          id: this.project._id
        } })
    }
  },

  watch: {
    payload: {
      deep: true,
      handler () {
        const totalDeployed = this.getTotalDeployed()
        const totalLoans = this.getTotalLoans()

        this.stats = [
          {
            stat: this.$formatting.toCurrency(this.payload.amount),
            text: 'Total Facility'
          },
          {
            stat: this.$formatting.toCurrency(totalLoans),
            text: 'Total Loans'
          },
          {
            stat: this.$formatting.toCurrency(totalDeployed),
            text: 'Total Deployed'
          }
        ]
      }
    }
  }
}
</script>
